import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import type { UpdateEvent } from '@intranet/modules/desktop/providers/visarc-desktop.global';

/** Internal state used to drive the in-app update banner. Wraps the lifecycle
 *  events from electron-updater (forwarded over IPC by the preload script)
 *  into a single rendering-friendly value. */
export type UpdateState =
    | { kind: 'idle' }
    | { kind: 'downloading'; version: string; percent: number }
    | { kind: 'ready'; version: string }
    | { kind: 'dismissed' };

/**
 * Subscribes to electron-updater events forwarded from the main process and
 * exposes a single observable the Angular UI can react to.
 *
 * Only active inside the Visarc Timer Electron shell — outside (a regular
 * browser tab) `window.visarcDesktop` is undefined, the service stays in
 * the `idle` state forever, and the `UpdateBannerComponent`'s `*ngIf` keeps
 * the banner hidden.
 */
@Injectable({ providedIn: 'root' })
export class UpdateService implements OnDestroy {
    private _state$ = new BehaviorSubject<UpdateState>({ kind: 'idle' });
    private unsubscribe: (() => void) | null = null;

    readonly state$: Observable<UpdateState> = this._state$.asObservable();

    constructor(private zone: NgZone) {
        const api = typeof window !== 'undefined' ? window.visarcDesktop : undefined;
        if (!api) return;

        // IPC events arrive outside Angular's zone — wrap callbacks so change
        // detection runs and the banner re-renders when state changes.
        this.unsubscribe = api.onUpdateEvent(event => {
            this.zone.run(() => this.applyEvent(event));
        });
    }

    /** Reduce a raw update event into the simpler rendering state. */
    private applyEvent(event: UpdateEvent): void {
        const current = this._state$.value;
        switch (event.type) {
            case 'available':
                this._state$.next({ kind: 'downloading', version: event.version, percent: 0 });
                return;
            case 'progress':
                if (current.kind === 'downloading') {
                    this._state$.next({ ...current, percent: event.percent });
                }
                return;
            case 'downloaded':
                // If the user previously dismissed the banner during download,
                // surface it again now that there's something to install.
                this._state$.next({ kind: 'ready', version: event.version });
                return;
            case 'error':
                // Stay quiet — errors usually mean "no network" or "404 latest.yml"
                // and bothering the user with them isn't useful. Console only.
                console.warn('[UpdateService] update event error:', event.message);
                return;
            // 'checking' and 'not-available' don't change the banner.
        }
    }

    /** Tells the main process to quit and install the downloaded update.
     *
     *  Note: this is immediate and unrecoverable — `quitAndInstall` force-quits
     *  the app, runs the installer, and relaunches. The caller is responsible
     *  for confirming with the user first (see UpdateBannerComponent's
     *  in-app confirm modal) since unsaved work in any open editor will be
     *  lost on quit. */
    async installNow(): Promise<void> {
        await window.visarcDesktop?.installUpdateNow();
    }

    /** Hide the banner for the current session — re-appears on next launch
     *  if the update still hasn't been applied. */
    dismiss(): void {
        this._state$.next({ kind: 'dismissed' });
    }

    ngOnDestroy(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
    }
}
